/**
 * CloudProvider — implementazione `WhatsAppProvider` per WhatsApp Business Cloud
 * (Standalone + Coexistence). Tutte le chiamate Graph API passano dall'edge function
 * `meta-cloud-proxy` (decifra `business_integration_token` server-side, applica
 * allowlist path, rate limit).
 *
 * Letture chat/messaggi: NON via Graph API polling. I messaggi arrivano via webhook
 * → `whatsapp_messages` table → realtime subscribe nel chat store. Quindi i metodi
 * `getChats()`/`getMessages()` qui ritornano `[]` — è lo store che legge dal DB.
 *
 * CSW state: letto da `whatsapp_csw_state` table (riempita dal webhook handler su
 * ogni inbound). Niente chiamata Meta.
 */

import { supabase } from '../../supabase';
import { e164FromAny } from './identity';
import { computeCSWState } from './csw';
import { proxyErrorInfo } from './cloudProxyError';
import {
  buildContactBody,
  buildLocationBody,
  buildReactionBody,
  buildReadBody,
  buildTypingBody,
  shouldSendTyping,
} from './cloudPayloads';
import {
  buildTemplateSendComponents,
  describeTemplate,
  renderTemplatePlainText,
  templateBlockReason,
} from './templateSend';
import { prepareCloudAudio } from './cloudAudio';
import type {
  WhatsAppProvider,
  WhatsAppProviderType,
  ProviderCapabilities,
  SendOptions,
  SendTemplateOptions,
  CSWState,
  TemplateMeta,
  QualityRating,
  ChatOverview,
  Message,
  PresenceStatus,
  WahaSendResult,
} from './types';

interface CloudProxyResponse<T = unknown> {
  ok: boolean;
  status: number;
  data?: T;
  error?: {
    code: number | string;
    message: string;
    type?: string;
    canonical_code?: MetaCanonicalCode;
    user_message?: string;
    retryable?: boolean;
    isTokenInvalid?: boolean;
  };
  token_invalid?: boolean;
}

/**
 * Codici errore canonici allineati a `_shared/metaErrorUtils.ts` (backend).
 * Esportati così l'UI può fare `error.name === 'WINDOW_CLOSED'` per intercettare casi specifici.
 */
export type MetaCanonicalCode =
  | 'TOKEN_INVALID'
  | 'BUSINESS_INTEGRATION_EXPIRED'
  | 'WINDOW_CLOSED'
  | 'RATE_LIMIT'
  | 'THROUGHPUT_LIMIT'
  | 'TEMPORARILY_BLOCKED'
  | 'PHONE_ALREADY_REGISTERED'
  | 'PHONE_REGISTRATION_FAILED'
  | 'TEMPLATE_REJECTED'
  | 'TEMPLATE_PARAM_MISMATCH'
  | 'RECIPIENT_INVALID'
  | 'MESSAGE_UNDELIVERED'
  | 'MEDIA_UPLOAD_FAILED'
  | 'INVALID_PARAMETER'
  | 'UNKNOWN';

/**
 * Eccezione lanciata dal CloudProvider quando una chiamata Meta fallisce con codice noto.
 * La UI intercetta tramite `error instanceof MetaError && error.canonicalCode === 'WINDOW_CLOSED'`
 * per aprire flussi specifici (es. ReengagementModal).
 */
export class MetaError extends Error {
  readonly canonicalCode: MetaCanonicalCode;
  readonly retryable: boolean;
  readonly statusCode: number;
  readonly rawCode: number | string | undefined;

  constructor(opts: {
    canonicalCode: MetaCanonicalCode;
    userMessage: string;
    retryable?: boolean;
    statusCode?: number;
    rawCode?: number | string;
  }) {
    super(opts.userMessage);
    this.name = opts.canonicalCode;
    this.canonicalCode = opts.canonicalCode;
    this.retryable = opts.retryable ?? false;
    this.statusCode = opts.statusCode ?? 502;
    this.rawCode = opts.rawCode;
  }
}

interface ProviderConfig {
  configId: string;
  organizationId: string;
  phoneNumberId: string;
  wabaId: string;
  isCoex: boolean;
}

const CAPABILITIES_STANDALONE: ProviderCapabilities = {
  groups: false,
  templates: true,
  catalog: true,
  payments: true,
  channels: false,
  csw_tracking: true,
  quality_rating: true,
  max_throughput_mps: 1000,
};

const CAPABILITIES_COEX: ProviderCapabilities = {
  ...CAPABILITIES_STANDALONE,
  catalog: false,
  payments: false,
  max_throughput_mps: 20,
};

export class CloudProvider implements WhatsAppProvider {
  readonly type: WhatsAppProviderType;
  readonly capabilities: ProviderCapabilities;
  private readonly cfg: ProviderConfig;
  /** Ultimo "sta scrivendo" mandato per chat (ms), per non superare uno ogni 20 s. */
  private readonly typingSentAt = new Map<string, number>();

  constructor(cfg: ProviderConfig) {
    this.cfg = cfg;
    this.type = cfg.isCoex ? 'whatsapp_cloud_coex' : 'whatsapp_cloud';
    this.capabilities = cfg.isCoex ? CAPABILITIES_COEX : CAPABILITIES_STANDALONE;
  }

  // ─── Letture: ritornate dal chat store via DB realtime ─────────────
  async getChats(): Promise<ChatOverview[]> {
    return [];
  }
  async getMessages(): Promise<Message[]> {
    return [];
  }
  async getProfilePicture(): Promise<string | null> {
    return null;
  }

  // ─── Send ───────────────────────────────────────────────────────────
  async sendText(chatId: string, text: string, opts?: SendOptions): Promise<WahaSendResult> {
    const to = e164FromAny(chatId);
    if (!to) throw new Error(`chatId non normalizzabile a E.164: "${chatId}"`);

    const body: Record<string, unknown> = {
      messaging_product: 'whatsapp',
      to,
      type: 'text',
      text: { body: text, preview_url: false },
    };
    if (opts?.replyToId) {
      body.context = { message_id: opts.replyToId };
    }

    const res = await this.proxy<{ messages: { id: string }[] }>('POST', `/${this.cfg.phoneNumberId}/messages`, body);
    return this.toSendResult(res);
  }

  async sendTemplate(chatId: string, opts: SendTemplateOptions): Promise<WahaSendResult> {
    const to = e164FromAny(chatId);
    if (!to) throw new Error(`chatId non normalizzabile a E.164: "${chatId}"`);

    // Struttura del modello approvato (salvata dal sync): serve per mettere ogni
    // variabile nel componente giusto, gestire le variabili a nome e l'allegato
    // dell'intestazione. Se non la troviamo resta il comportamento storico.
    const { data: tpl } = await supabase
      .from('whatsapp_templates')
      .select('components')
      .eq('config_id', this.cfg.configId)
      .eq('name', opts.templateName)
      .eq('language', opts.language)
      .maybeSingle();
    let components: unknown[];
    let displayText = opts.displayText;
    if (tpl?.components) {
      const shape = describeTemplate(tpl.components);
      const blocked = templateBlockReason(shape, opts.variables);
      if (blocked) {
        throw new MetaError({ canonicalCode: 'TEMPLATE_PARAM_MISMATCH', userMessage: blocked, statusCode: 400 });
      }
      components = buildTemplateSendComponents(shape, opts.variables);
      if (opts.buttonUrlOverride && !components.some((c) => (c as { type?: string }).type === 'button')) {
        components.push(...buildTemplateComponents({ ...opts, variables: undefined, headerVariables: undefined }));
      }
      displayText = displayText || renderTemplatePlainText(shape, opts.variables) || undefined;
    } else {
      components = buildTemplateComponents(opts);
    }
    const body = {
      messaging_product: 'whatsapp',
      to,
      type: 'template',
      template: {
        name: opts.templateName,
        language: { code: opts.language },
        ...(components.length > 0 ? { components } : {}),
      },
    };
    const res = await this.proxy<{ messages: { id: string }[] }>(
      'POST',
      `/${this.cfg.phoneNumberId}/messages`,
      body,
      undefined,
      displayText ? { display_text: displayText } : undefined,
    );
    return this.toSendResult(res);
  }

  async sendImage(chatId: string, image: File, caption?: string, opts?: SendOptions): Promise<WahaSendResult> {
    return this.sendMediaMessage(chatId, 'image', image, caption, opts);
  }
  async sendVideo(chatId: string, video: File, caption?: string, opts?: SendOptions): Promise<WahaSendResult> {
    return this.sendMediaMessage(chatId, 'video', video, caption, opts);
  }
  async sendFile(chatId: string, file: File, caption?: string, opts?: SendOptions): Promise<WahaSendResult> {
    return this.sendMediaMessage(chatId, 'document', file, caption, opts, file.name);
  }
  /**
   * Vocale/audio: Meta accetta solo Ogg/Opus come vocale (e non WebM, che e'
   * quello che registra Chrome anche se il file si chiama .ogg). prepareCloudAudio
   * guarda il contenuto, converte WebM->Ogg senza ricodificare, e decide il tipo
   * giusto (vedi cloudAudio.ts). Formati non accettati: errore chiaro.
   */
  async sendVoice(chatId: string, audio: Blob, opts?: SendOptions): Promise<WahaSendResult> {
    const prepared = await prepareCloudAudio(audio);
    return this.sendMediaMessage(chatId, 'audio', prepared.file, undefined, opts, undefined, prepared.voice, prepared.filename);
  }

  async sendReaction(chatId: string, messageId: string, emoji: string): Promise<void> {
    const to = this.toE164(chatId);
    await this.proxy('POST', `/${this.cfg.phoneNumberId}/messages`, buildReactionBody(to, messageId, emoji));
  }

  async sendLocation(chatId: string, latitude: number, longitude: number, name?: string, address?: string): Promise<WahaSendResult> {
    const to = this.toE164(chatId);
    const res = await this.proxy<{ messages: { id: string }[] }>(
      'POST', `/${this.cfg.phoneNumberId}/messages`, buildLocationBody(to, latitude, longitude, name, address),
    );
    return this.toSendResult(res);
  }

  async sendContact(chatId: string, contact: { name: string; phone: string }): Promise<WahaSendResult> {
    const to = this.toE164(chatId);
    const res = await this.proxy<{ messages: { id: string }[] }>(
      'POST', `/${this.cfg.phoneNumberId}/messages`, buildContactBody(to, contact),
    );
    return this.toSendResult(res);
  }

  /** Carica un file su Meta (resta privato, niente link pubblico) e ne restituisce l'id. */
  async uploadMediaFile(file: Blob, filename?: string): Promise<string> {
    return this.uploadMedia(file, filename);
  }

  private toE164(chatId: string): string {
    const to = e164FromAny(chatId);
    if (!to) throw new Error(`chatId non normalizzabile a E.164: "${chatId}"`);
    return to;
  }

  /**
   * Invio media Cloud (MULTIPART): 1) upload binario a Meta via proxy → media id,
   * 2) POST /messages con `{ type, <type>:{ id, caption?, filename? } }`.
   */
  private async sendMediaMessage(
    chatId: string,
    mediaType: 'image' | 'video' | 'document' | 'audio',
    file: Blob,
    caption?: string,
    opts?: SendOptions,
    filename?: string,
    voice?: boolean,
    uploadName?: string,
  ): Promise<WahaSendResult> {
    const to = e164FromAny(chatId);
    if (!to) throw new Error(`chatId non normalizzabile a E.164: "${chatId}"`);

    const mediaId = await this.uploadMedia(file, uploadName ?? filename);
    const mediaObj: Record<string, unknown> = { id: mediaId };
    if (caption && mediaType !== 'audio') mediaObj.caption = caption;
    if (mediaType === 'document' && filename) mediaObj.filename = filename;
    if (mediaType === 'audio' && voice) mediaObj.voice = true;

    const body: Record<string, unknown> = {
      messaging_product: 'whatsapp',
      to,
      type: mediaType,
      [mediaType]: mediaObj,
    };
    if (opts?.replyToId) body.context = { message_id: opts.replyToId };

    // media_mimetype: il proxy lo salva in chat insieme al segnaposto meta:<id>,
    // cosi' l'anteprima resta anche dopo il ricaricamento.
    const res = await this.proxy<{ messages: { id: string }[] }>(
      'POST', `/${this.cfg.phoneNumberId}/messages`, body, undefined,
      { media_mimetype: (file.type || '').split(';')[0] || undefined },
    );
    return this.toSendResult(res);
  }

  /** Upload binario a Meta via proxy (multipart server-side). Ritorna il media id. */
  private async uploadMedia(file: Blob, filename?: string): Promise<string> {
    const mime = file.type || 'application/octet-stream';
    const base64 = await blobToBase64(file);
    const { data, error } = await supabase.functions.invoke('meta-cloud-proxy', {
      body: {
        config_id: this.cfg.configId,
        organization_id: this.cfg.organizationId,
        graph_method: 'POST',
        graph_path: `/${this.cfg.phoneNumberId}/media`,
        media_upload: { base64, mime, filename: filename || (file instanceof File ? file.name : undefined) },
      },
    });
    if (error) throw new Error(`upload media: ${error.message}`);
    const res = data as CloudProxyResponse<{ id: string }>;
    if (!res.ok || !res.data?.id) {
      const msg = res.error?.user_message || res.error?.message || 'Upload media fallito';
      throw new MetaError({
        canonicalCode: (res.error?.canonical_code as MetaCanonicalCode) ?? 'MEDIA_UPLOAD_FAILED',
        userMessage: msg,
        statusCode: res.status,
        rawCode: res.error?.code,
      });
    }
    return res.data.id;
  }

  // ─── Stato chat ─────────────────────────────────────────────────────
  async markAsRead(chatId: string): Promise<void> {
    // Cloud: mark-as-read richiede il wamid di un messaggio inbound. Recuperiamo
    // l'ultimo inbound della chat dal DB e segnaliamo 'read' (spunte blu al cliente).
    const wamid = await this.lastInboundWamid(chatId);
    if (!wamid) return;
    try {
      await this.proxy('POST', `/${this.cfg.phoneNumberId}/messages`, buildReadBody(wamid));
    } catch {
      /* best-effort: il mark-as-read non deve mai bloccare l'UI */
    }
  }

  /**
   * "Sta scrivendo" (doc Meta Typing indicators): status read + typing_indicator
   * sull'ultimo messaggio del cliente; resta 25 s o fino alla risposta. Lo
   * mandiamo al massimo ogni 20 s per chat e solo se il cliente ha scritto nelle
   * ultime 24 ore (fuori finestra non si puo' rispondere). 'paused' non esiste
   * su Cloud: l'indicatore scade da solo.
   */
  async setPresence(chatId: string, status: PresenceStatus): Promise<void> {
    if (status !== 'typing') return;
    const now = Date.now();
    if (!shouldSendTyping(this.typingSentAt.get(chatId), now)) return;
    this.typingSentAt.set(chatId, now);
    const wamid = await this.lastInboundWamid(chatId, 24 * 3600 * 1000);
    if (!wamid) return;
    try {
      await this.proxy('POST', `/${this.cfg.phoneNumberId}/messages`, buildTypingBody(wamid));
    } catch {
      /* best-effort */
    }
  }

  /** wamid dell'ultimo messaggio del cliente in questa chat (opzionale: non piu' vecchio di maxAgeMs). */
  private async lastInboundWamid(chatId: string, maxAgeMs?: number): Promise<string | null> {
    const e164 = e164FromAny(chatId);
    if (!e164) return null;
    let query = supabase
      .from('whatsapp_messages')
      .select('id')
      .eq('config_id', this.cfg.configId)
      .eq('chat_identifier_e164', e164)
      .eq('direction', 'inbound');
    if (maxAgeMs) query = query.gte('created_at', new Date(Date.now() - maxAgeMs).toISOString());
    const { data } = await query.order('created_at', { ascending: false }).limit(1).maybeSingle();
    return (data?.id as string | undefined) ?? null;
  }

  // ─── Cloud-only ─────────────────────────────────────────────────────
  async getCSWState(chatId: string): Promise<CSWState> {
    const e164 = e164FromAny(chatId);
    if (!e164) {
      return { cswExpiresAt: null, fepExpiresAt: null, isOpen: false, minutesRemaining: null };
    }
    const { data } = await supabase
      .from('whatsapp_csw_state')
      .select('csw_expires_at, fep_expires_at')
      .eq('config_id', this.cfg.configId)
      .eq('wa_id', e164)
      .maybeSingle();

    if (!data) {
      return { cswExpiresAt: null, fepExpiresAt: null, isOpen: false, minutesRemaining: null };
    }
    return computeCSWState(data.csw_expires_at, data.fep_expires_at);
  }

  async getTemplates(): Promise<TemplateMeta[]> {
    const { data } = await supabase
      .from('whatsapp_templates')
      .select('id, meta_template_id, name, language, category, status, rejection_reason, effective_category, components')
      .eq('config_id', this.cfg.configId)
      .order('updated_at', { ascending: false });
    return (data || []).map((r) => ({
      id: r.meta_template_id || r.id,
      name: r.name,
      language: r.language,
      category: r.category as TemplateMeta['category'],
      status: r.status as TemplateMeta['status'],
      rejectionReason: r.rejection_reason || undefined,
      effectiveCategory: r.effective_category || undefined,
      components: (r.components as unknown[]) || [],
    }));
  }

  async getQualityRating(): Promise<QualityRating> {
    const res = await this.proxy<{
      quality_rating?: string;
      messaging_limit?: string;
      name_status?: string;
    }>('GET', `/${this.cfg.phoneNumberId}`, undefined, {
      fields: 'quality_rating,messaging_limit,name_status',
    });
    const data = res.data || {};
    return {
      rating: (data.quality_rating as QualityRating['rating']) || 'UNKNOWN',
      tier: (data.messaging_limit as QualityRating['tier']) || null,
      displayNameStatus: (data.name_status as QualityRating['displayNameStatus']) || null,
    };
  }

  // ─── Internal: chiamata edge fn proxy ───────────────────────────────
  private async proxy<T = unknown>(
    method: string,
    path: string,
    body?: unknown,
    query?: Record<string, string | number | boolean | undefined>,
    extra?: Record<string, unknown>,
  ): Promise<CloudProxyResponse<T>> {
    const { data, error } = await supabase.functions.invoke('meta-cloud-proxy', {
      body: {
        config_id: this.cfg.configId,
        organization_id: this.cfg.organizationId,
        graph_method: method,
        graph_path: path,
        graph_body: body,
        graph_query: query,
        ...(extra ?? {}),
      },
    });
    if (error) {
      // Risposta con codice di errore: invoke non da' `data`, il corpo e' in error.context (Response).
      let body: unknown = null;
      let httpStatus: number | undefined;
      const ctx = (error as { context?: unknown }).context;
      if (ctx && typeof (ctx as Response).json === 'function') {
        httpStatus = (ctx as Response).status;
        try { body = await (ctx as Response).clone().json(); } catch { body = null; }
      }
      console.warn('[CloudProvider] meta-cloud-proxy errore', { status: httpStatus, body, message: error.message });
      this.throwProxyError(body, httpStatus);
    }
    const res = data as CloudProxyResponse<T>;
    if (!res?.ok) {
      console.warn('[CloudProvider] meta-cloud-proxy risposta non riuscita', res);
      this.throwProxyError(res, res?.status);
    }
    return res;
  }

  /** Lancia MetaError con frase italiana e codice letti dal corpo della risposta del proxy. */
  private throwProxyError(body: unknown, httpStatus?: number): never {
    const info = proxyErrorInfo(body, httpStatus);
    // 131042 = "Business eligibility payment issue": metodo di pagamento mancante/
    // esaurito. Accendiamo il flag così la checklist mostra l'alert (non pollabile).
    if (Number(info.rawCode) === 131042) void this.setPaymentIssue(true);
    // canonicalCode permette alla UI di intercettare casi specifici (es. WINDOW_CLOSED → modelli).
    throw new MetaError({
      canonicalCode: info.canonicalCode as MetaCanonicalCode,
      userMessage: info.userMessage,
      retryable: info.retryable,
      statusCode: info.statusCode,
      rawCode: info.rawCode,
    });
  }

  private toSendResult(res: CloudProxyResponse<{ messages: { id: string }[] }>): WahaSendResult {
    const wamid = res.data?.messages?.[0]?.id;
    if (!wamid) throw new Error("WhatsApp non ha confermato l'invio: controlla tra poco se il messaggio e' partito.");
    // Invio riuscito → eventuale flag pagamento si spegne.
    void this.setPaymentIssue(false);
    // Timestamp in SECONDI per coerenza con WahaSendResult (lo store fa `timestamp * 1000`).
    return { id: wamid, timestamp: Math.floor(Date.now() / 1000), status: 'sent' };
  }

  /**
   * Aggiorna `configuration.payment_issue` (best-effort, no-op se invariato).
   * Lo stato pagamento NON è leggibile via Graph API: lo deduciamo dall'errore 131042
   * all'invio (set true) e lo azzeriamo al primo invio riuscito (set false).
   */
  private async setPaymentIssue(value: boolean): Promise<void> {
    try {
      const { data } = await supabase
        .from('api_configurations')
        .select('configuration')
        .eq('id', this.cfg.configId)
        .maybeSingle();
      const cfg = (data?.configuration as Record<string, unknown> | null) || {};
      if (!!cfg.payment_issue === value) return;
      await supabase
        .from('api_configurations')
        .update({ configuration: { ...cfg, payment_issue: value } })
        .eq('id', this.cfg.configId);
    } catch {
      /* best-effort: non bloccare l'invio per il tracking del flag */
    }
  }
}

// computeCSWState è stato estratto nel modulo puro './csw' (testabile senza supabase).
// Re-export per retrocompatibilità con gli importatori che lo prendono da CloudProvider.
export { computeCSWState } from './csw';

/** Converte un Blob/File in base64 puro (senza prefisso data:). */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Costruisce l'array `components` Meta-format dal SendTemplateOptions.
 * Si limita a body/header/buttons più comuni. Per i casi avanzati il caller
 * dovrà passare components custom (estensione future).
 */
function buildTemplateComponents(opts: SendTemplateOptions): unknown[] {
  const components: unknown[] = [];

  if (opts.headerVariables) {
    const params = Object.values(opts.headerVariables).map((value) => ({
      type: 'text',
      text: String(value),
    }));
    if (params.length > 0) {
      components.push({ type: 'header', parameters: params });
    }
  }

  if (opts.variables) {
    const params = Object.values(opts.variables).map((value) => ({
      type: 'text',
      text: String(value),
    }));
    if (params.length > 0) {
      components.push({ type: 'body', parameters: params });
    }
  }

  if (opts.buttonUrlOverride) {
    components.push({
      type: 'button',
      sub_type: 'url',
      index: '0',
      parameters: [{ type: 'text', text: opts.buttonUrlOverride }],
    });
  }

  return components;
}
